import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Sparkle } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { DESTINATIONS } from "@/data/content";
import { BLOG_POSTS } from "@/data/blogs";
import { listAllDestinations } from "@/data/routes";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/PhoneCTA";
import { useSEO, breadcrumbsSchema, SITE_URL } from "@/lib/seo";

// Map fine-grained region strings → macro region used by the filter pills.
// Anything unmapped falls under "Other" so featured destinations without a
// blog grouping (e.g. Tashkent in Central Asia) still appear in the grid.
const REGION_MAP = [
  { key: "asia",      label: "Asia",          patterns: [/asia/i, /uae|emirates/i, /middle east/i] },
  { key: "europe",    label: "Europe",        patterns: [/europe/i, /iberia/i, /mediterranean/i, /cyclades/i, /liguria/i, /île-de-france/i] },
  { key: "pacific",   label: "Pacific",       patterns: [/pacific/i, /oceania/i] },
  { key: "americas",  label: "Americas",      patterns: [/americas/i, /caribbean/i, /antilles/i, /caribbean coast/i] },
  { key: "nordic",    label: "Nordic",        patterns: [/nordic/i, /scandinavia/i] },
  { key: "british",   label: "British Isles", patterns: [/british isles/i] },
];

function macroRegion(region) {
  if (!region) return "other";
  for (const r of REGION_MAP) {
    if (r.patterns.some((p) => p.test(region))) return r.key;
  }
  return "other";
}

function DestinationCard({ d }) {
  return (
    <Link
      to={`/destinations/${d.code}`}
      data-testid={`destinations-index-card-${d.code}`}
      className="group relative overflow-hidden aspect-[4/5] lift bg-[#1C1C1C]"
    >
      {d.img && (
        <img
          src={d.img}
          alt={`${d.city}, ${d.country}`}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      <div className="absolute inset-0 p-5 md:p-7 flex flex-col justify-between text-[#F7F5F0]">
        <div className="flex items-start justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] tracking-[0.18em] uppercase bg-black/40 backdrop-blur-sm">
            {d.featured && <Sparkle size={10} weight="fill" className="text-[#C29B62]" />}
            {d.country}
          </span>
          <span className="text-[10px] tracking-[0.18em] uppercase text-[#A09E9A]">{d.code}</span>
        </div>
        <div>
          <div className="flex items-end justify-between gap-2 mb-2">
            <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-none">{d.city}</h3>
            <ArrowRight size={22} className="text-[#C29B62] shrink-0 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
          </div>
          {d.tagline && (
            <p className="font-serif italic text-sm text-[#A09E9A] leading-relaxed line-clamp-2">
              {d.tagline}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function DestinationsIndex() {
  const [params, setParams] = useSearchParams();
  const activeRegion = (params.get("region") || "all").toLowerCase();

  const destinations = useMemo(() => {
    const all = listAllDestinations({ blogPosts: BLOG_POSTS, featured: DESTINATIONS });
    return all.map((d) => ({ ...d, macroRegion: macroRegion(d.region) }));
  }, []);

  // Build counts per region so we can render badges like "Asia · 4".
  const counts = useMemo(() => {
    const c = { all: destinations.length };
    for (const r of REGION_MAP) c[r.key] = 0;
    for (const d of destinations) {
      if (c[d.macroRegion] !== undefined) c[d.macroRegion]++;
    }
    return c;
  }, [destinations]);

  const visible = activeRegion === "all"
    ? destinations
    : destinations.filter((d) => d.macroRegion === activeRegion);

  const setRegion = (key) => {
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("region");
    else next.set("region", key);
    setParams(next, { replace: false });
  };

  // SEO — title shifts with the active region for shareable filtered URLs.
  const activeLabel = REGION_MAP.find((r) => r.key === activeRegion)?.label;
  useSEO({
    title: activeLabel
      ? `${activeLabel} Destinations | TheCityPair`
      : "Destinations — Premium Flight Reservations | TheCityPair",
    description: activeLabel
      ? `Editor-curated ${activeLabel} destinations our travel desk books most. Live fares, dedicated landing pages, and a real specialist on call.`
      : "Editor-curated destinations our travel desk books most. From Santorini and Sydney to Reykjavik and Havana — each with its own page, live fares, and a real specialist on call.",
    canonical: activeRegion === "all" ? "/destinations" : `/destinations?region=${activeRegion}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": `${SITE_URL}/destinations`,
        name: activeLabel ? `${activeLabel} Destinations` : "Destinations",
        url: `${SITE_URL}/destinations`,
        hasPart: visible.map((d) => ({
          "@type": "TouristDestination",
          name: `${d.city}, ${d.country}`,
          url: `${SITE_URL}/destinations/${d.code}`,
          image: d.img,
        })),
      },
      breadcrumbsSchema([
        { name: "Home", url: "/" },
        { name: "Destinations", url: "/destinations" },
      ]),
    ],
  });

  useEffect(() => { window.scrollTo(0, 0); }, [activeRegion]);

  const pills = [
    { key: "all", label: "All" },
    ...REGION_MAP.filter((r) => counts[r.key] > 0).map((r) => ({ key: r.key, label: r.label })),
  ];

  return (
    <div className="bg-[#F7F5F0] min-h-screen" data-testid="destinations-index-page">
      <div className="bg-[#0A0A0B] relative"><Navbar dark /><div className="h-24" /></div>

      {/* Editorial header */}
      <header className="px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-10 md:pb-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-3xl">
            <p className="tcp-overline text-[#C29B62] mb-4">Destinations</p>
            <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95] text-[#1C1C1C]">
              {destinations.length} cities. <em className="italic">One travel desk.</em>
            </h1>
            <p className="text-[#666460] mt-6 max-w-xl leading-relaxed">
              Each destination has its own editorial page, live fares from your nearest US gateway,
              and a real specialist on call when you're ready to book.
            </p>
          </div>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="destinations-index-call"
            className="self-start md:self-end inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#C29B62] hover:text-[#A88550] border-b border-[#C29B62] hover:border-[#A88550] pb-1 whitespace-nowrap transition-colors"
          >
            <Phone size={14} weight="fill" /> Talk to a specialist · {PHONE_DISPLAY}
          </a>
        </div>
      </header>

      {/* Region filter pills */}
      <nav className="px-6 md:px-12 lg:px-24 pb-8" aria-label="Filter destinations by region">
        <div className="flex flex-wrap gap-2 md:gap-3" data-testid="destinations-filter-bar">
          {pills.map((p) => {
            const active = p.key === activeRegion;
            return (
              <button
                key={p.key}
                onClick={() => setRegion(p.key)}
                data-testid={`destinations-filter-${p.key}`}
                aria-pressed={active}
                className={
                  "inline-flex items-center gap-2 px-4 py-2 text-xs tracking-[0.16em] uppercase border transition-colors " +
                  (active
                    ? "bg-[#1C1C1C] text-[#F7F5F0] border-[#1C1C1C]"
                    : "bg-white text-[#1C1C1C] border-[#E0DDD6] hover:border-[#C29B62] hover:text-[#C29B62]")
                }
              >
                {p.label}
                <span className={active ? "text-[#C29B62]" : "text-[#A09E9A]"}>
                  {counts[p.key]}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      <section className="px-6 md:px-12 lg:px-24 pb-24" data-testid="destinations-grid">
        {visible.length === 0 ? (
          <div className="bg-white border border-[#E0DDD6] p-10 text-center" data-testid="destinations-empty">
            <p className="font-serif text-2xl text-[#1C1C1C] mb-2">No destinations in that region yet.</p>
            <p className="text-[#666460] text-sm">Try "All" or call us — our desk books everywhere.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {visible.map((d) => <DestinationCard key={d.code} d={d} />)}
          </div>
        )}
        <div className="mt-12 pt-6 border-t border-[#E0DDD6] flex items-center gap-3 text-xs tracking-[0.18em] uppercase text-[#666460]">
          <MapPin size={14} className="text-[#C29B62]" weight="fill" />
          <span>Don't see your destination? Call us — our desk books everywhere.</span>
        </div>
      </section>

      <Footer />
    </div>
  );
}
